import { z } from "zod";

/**
 * Generic Selection Schema
 * Farklı ürünler (otobüs, uçak, otel vb.) için kullanılabilir genel bir seçim şeması
 */

// Selection item types
export const SELECTION_TYPES = {
  INCREMENT: "inc", // Increment/decrement selector
  GROUP: "group", // Grouping container
  RADIO: "radio", // Radio button group
  CHECKBOX: "checkbox", // Checkbox
  TOGGLE: "toggle", // Toggle switch
};

// Generic selection item schema
const selectionItemSchema = z.object({
  type: z.enum([
    SELECTION_TYPES.INCREMENT,
    SELECTION_TYPES.GROUP,
    SELECTION_TYPES.RADIO,
    SELECTION_TYPES.CHECKBOX,
    SELECTION_TYPES.TOGGLE,
  ]),
  slug: z.string().optional(), // Optional for GROUP type
  label: z.string().optional(), // Can be translation key or direct text
  labelKey: z.string().optional(), // Translation key (priority over label)
  description: z.string().optional(), // Can be translation key or direct text
  descriptionKey: z.string().optional(), // Translation key (priority over description)
  className: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  value: z.any().optional(),
  badge: z.string().optional(), // Can be translation key or direct text
  badgeKey: z.string().optional(), // Translation key (priority over badge)
  icon: z.string().optional(),
  disabled: z.boolean().optional(),
  hidden: z.boolean().optional(),
  children: z.array(z.lazy(() => selectionItemSchema)).optional(),
  // Custom validation rules
  validation: z
    .object({
      required: z.boolean().optional(),
      customRule: z.function().optional(),
      errorMessage: z.string().optional(),
      errorMessageKey: z.string().optional(), // Translation key for error message
    })
    .optional(),
});

// Settings schema
const settingsSchema = z.object({
  totalSelections: z.number().optional(), // Maksimum toplam seçim sayısı
  minTotalSelections: z.number().optional(), // Minimum toplam seçim sayısı
  allowZeroSelections: z.boolean().optional(), // Tüm seçimler 0 olabilir mi
  // Opsiyonel genel validasyon fonksiyonu
  // İmza: (values, incrementValues, settings) => { errors?: Record<string,string> } | void
  generalValidations: z.function().optional(),
  customValidations: z
    .array(
      z.object({
        rule: z.function(),
        message: z.string(),
        messageKey: z.string().optional(), // Translation key for message
        path: z.string(),
      }),
    )
    .optional(),
  // Tamam'a basıldığında çalışır, values => { valid: boolean, general?: object }
  applyValidation: z.function().optional(),
});

// Main configuration schema
export const configurationSchema = z.object({
  settings: settingsSchema.optional(),
  children: z.array(selectionItemSchema),
});

/**
 * Create a custom validation function based on configuration
 * @param {Object} config - Configuration object
 * @returns {Function} Validation function
 */
export const createValidator = (config) => {
  return (values) => {
    const errors = {};
    const warnings = {};

    // Parse config
    const parsedConfig = configurationSchema.parse(config);
    const settings = parsedConfig.settings || {};

    // Extract all increment values
    const getAllIncrementValues = (items, values) => {
      const result = {};
      items.forEach((item) => {
        if (item.type === SELECTION_TYPES.INCREMENT && item.slug) {
          result[item.slug] = values[item.slug] || item.value || 0;
        }
        if (item.children && item.children.length > 0) {
          Object.assign(result, getAllIncrementValues(item.children, values));
        }
      });
      return result;
    };

    const incrementValues = getAllIncrementValues(
      parsedConfig.children,
      values,
    );

    // Eğer config bir generalValidations fonksiyonu sağlıyorsa, yerleşik genel kurallar yerine onu kullan
    if (typeof settings.generalValidations === "function") {
      try {
        const result = settings.generalValidations(
          values,
          incrementValues,
          settings,
        );
        if (result && typeof result === "object") {
          if (result.errors) {
            Object.assign(errors, result.errors);
          }
          if (result.warnings) {
            Object.assign(warnings, result.warnings);
          }
        }
      } catch (error) {
        console.error("General validations function error:", error);
      }
    } else {
      // Yerleşik genel kurallar
      // Validate total selections
      if (settings.totalSelections !== undefined) {
        const total = Object.values(incrementValues).reduce(
          (sum, val) => sum + (typeof val === "number" ? val : 0),
          0,
        );
        if (total > settings.totalSelections) {
          errors.general = `Maksimum ${settings.totalSelections} seçim yapabilirsiniz`;
        }
      }

      // Validate min total selections
      if (settings.minTotalSelections !== undefined) {
        const total = Object.values(incrementValues).reduce(
          (sum, val) => sum + (typeof val === "number" ? val : 0),
          0,
        );
        if (total < settings.minTotalSelections) {
          errors.general = `En az ${settings.minTotalSelections} seçim yapmalısınız`;
        }
      }

      // Validate zero selections
      if (!settings.allowZeroSelections) {
        const total = Object.values(incrementValues).reduce(
          (sum, val) => sum + (typeof val === "number" ? val : 0),
          0,
        );
        if (total === 0) {
          errors.general = "En az bir seçim yapmalısınız";
        }
      }
    }

    // Custom validations
    if (settings.customValidations && settings.customValidations.length > 0) {
      settings.customValidations.forEach(({ rule, message, path }) => {
        try {
          if (!rule(values)) {
            errors[path] = message;
          }
        } catch (error) {
          console.error("Custom validation error:", error);
        }
      });
    }

    // Item-level validations
    const validateItems = (items) => {
      items.forEach((item) => {
        if (item.type === SELECTION_TYPES.INCREMENT && item.slug) {
          const value = values[item.slug] || item.value || 0;

          // Min/Max validation
          if (item.min !== undefined && value < item.min) {
            errors[item.slug] = `Minimum ${item.min} olmalıdır`;
          }
          if (item.max !== undefined && value > item.max) {
            errors[item.slug] = `Maksimum ${item.max} olabilir`;
          }

          // Required validation
          if (item.validation?.required && value === 0) {
            errors[item.slug] =
              item.validation.errorMessage || "Bu alan zorunludur";
          }

          // Custom rule validation
          if (item.validation?.customRule) {
            try {
              if (!item.validation.customRule(value, values)) {
                errors[item.slug] =
                  item.validation.errorMessage || "Geçersiz değer";
              }
            } catch (error) {
              console.error("Custom item validation error:", error);
            }
          }
        }

        // Recursive validation for children
        if (item.children && item.children.length > 0) {
          validateItems(item.children);
        }
      });
    };

    validateItems(parsedConfig.children);

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
      warnings,
    };
  };
};

/**
 * Extract default values from configuration
 * @param {Object} config - Configuration object
 * @returns {Object} Default values
 */
export const extractDefaultValues = (config) => {
  const values = {};

  const extractFromItems = (items) => {
    items.forEach((item) => {
      if (item.slug && item.value !== undefined) {
        values[item.slug] = item.value;
      }
      if (item.children && item.children.length > 0) {
        extractFromItems(item.children);
      }
    });
  };

  if (config.children) {
    extractFromItems(config.children);
  }

  return values;
};

/**
 * Get display text for current selections
 * @param {Object} config - Configuration object
 * @param {Object} values - Current values
 * @param {Function} formatter - Optional custom formatter function
 * @returns {String} Display text
 */
export const getDisplayText = (config, values, formatter) => {
  if (formatter) {
    return formatter(values, config);
  }

  // Default formatter
  const incrementValues = [];
  const extractIncrements = (items) => {
    items.forEach((item) => {
      if (item.type === SELECTION_TYPES.INCREMENT && item.slug) {
        const value = values[item.slug] || 0;
        if (value > 0) {
          incrementValues.push(`${value} ${item.label}`);
        }
      }
      if (item.children) {
        extractIncrements(item.children);
      }
    });
  };

  if (config.children) {
    extractIncrements(config.children);
  }

  return incrementValues.length > 0
    ? incrementValues.join(", ")
    : "Seçim yapın";
};
